import { isAuthenticationFresh } from './fresh-authentication.js';
/**
 * The second factor, normalised out of the token (TM-9636 / #1626, #1598).
 *
 * ⚠ **`amr` and `auth_time`, never `acr`.** An `acr` is a number that only gets
 * a meaning through the `acr.loa.map` map on the Keycloak client. Whoever
 * changes it changes the meaning of every check that reads the number, without
 * a test going red. `amr` names **what ran** (RFC 8176), and `auth_time`
 * **when**; both stand in the token and hang on no map.
 *
 * The house's only `acr` reader stays the email change (`acr < 1`). It is
 * neither touched nor replaced here: it asks a different question (*"was the
 * password entered just now"*).
 *
 * ⚠ **On the move to better-auth (ADR-0018) this information has to survive.**
 * An identity provider without `amr`/`auth_time` makes the Versand release fall
 * silently to `null`, and `null` means *"refuse"* here: the Versand would stand
 * still rather than wrongly let something through. So that this does not happen
 * unnoticed, the requirement stands at the port too; see
 * `identity-provider.port.ts`.
 */
/** What actually ran as a second factor. */
export var SecondFactorType;
(function (SecondFactorType) {
    /** A time-based one-time password from an authenticator app. */
    SecondFactorType["TOTP"] = "TOTP";
    /** A WebAuthn credential — passkey, security key, platform authenticator. */
    SecondFactorType["PASSKEY"] = "PASSKEY";
    /**
     * One of the recovery codes shown once at enrolment (TM-9636 / #1627).
     *
     * ⚠ **A member of its own and not a reinterpretation as TOTP.** The practice
     * reads on the run what it was released with; a code passing itself off as a
     * TOTP there would promise a device that has just been lost. It also counts
     * exactly once — Keycloak strikes it out on redemption.
     *
     * ⚠ **Appended at the end.** The `enum` column on the billing run stores the
     * index, not the text; a member in the middle would silently reinterpret
     * every existing row behind it.
     */
    SecondFactorType["RECOVERY_CODE"] = "RECOVERY_CODE";
})(SecondFactorType || (SecondFactorType = {}));
/**
 * The `amr` markers that count as a second factor.
 *
 * ⚠ **An allowlist.** The other way round (everything except `sms` and `email`)
 * would silently turn every future Keycloak module into a second factor.
 */
const TOTP_MARKERS = new Set(['otp', 'totp']);
const PASSKEY_MARKERS = new Set([
    'hwk',
    'swk',
    'webauthn',
    'webauthn-passwordless',
]);
/**
 * The recovery code's marker (TM-9636 / #1627).
 *
 * ⚠ **RFC 8176 knows none.** `rc` is therefore home-made and has to be the same
 * in two places: here and as `default.reference.value` on the
 * `auth-recovery-authn-code-form` of both realms. So that it is only one place,
 * the string stands here and the realm spec reads it from here; a realm with a
 * different marker otherwise turns a redeemed recovery code into *"no second
 * factor"*.
 */
export const RECOVERY_CODE_AMR_MARKER = 'rc';
const RECOVERY_CODE_MARKERS = new Set([RECOVERY_CODE_AMR_MARKER]);
/**
 * Markers that **cancel** a factor, even with `otp` beside them (#1598).
 *
 * RFC 8176 gives `otp` for every one-time password, including the one by SMS.
 * If one of these channels stands in the token, `otp` is exactly that one. A
 * password over the same channel the password is also reset over is not a
 * second factor.
 */
const CHANNEL_MARKERS = new Set(['sms', 'tel', 'email', 'mail']);
/** Ten minutes. A release should hang on the person sitting there right now. */
export const SECOND_FACTOR_WINDOW_SECONDS_DEFAULT = 600;
/**
 * This token's second factor, or `null`.
 *
 * ⚠ **A passkey beats a TOTP, and both beat the recovery code.** If several
 * ran, the strongest stands in the release: that is the information the
 * practice reads later, and it should name the factor that cannot be typed out.
 */
export function deriveSecondFactor(claims) {
    const { amr, auth_time: authTime } = claims;
    if (!Array.isArray(amr))
        return null;
    const markers = new Set(amr.filter((entry) => typeof entry === 'string'));
    if ([...markers].some((marker) => CHANNEL_MARKERS.has(marker)))
        return null;
    const type = [...markers].some((marker) => PASSKEY_MARKERS.has(marker))
        ? SecondFactorType.PASSKEY
        : [...markers].some((marker) => TOTP_MARKERS.has(marker))
            ? SecondFactorType.TOTP
            : [...markers].some((marker) => RECOVERY_CODE_MARKERS.has(marker))
                ? SecondFactorType.RECOVERY_CODE
                : null;
    if (type === null)
        return null;
    // ⚠ No moment, no factor: freshness is half the promise, and a factor
    // without `auth_time` could be three weeks old.
    if (typeof authTime !== 'number' || !Number.isFinite(authTime))
        return null;
    return { type, at: new Date(authTime * 1000).toISOString() };
}
/**
 * Whether this factor is young enough for a Versand release.
 *
 * The window and the tolerance for a slow clock stand in
 * {@link isAuthenticationFresh}.
 */
export function isSecondFactorFresh(evidence, now, windowSeconds) {
    return isAuthenticationFresh(evidence?.at ?? null, now, windowSeconds);
}
/**
 * The second factor an auth context carries — or `null`.
 *
 * ⚠ **Both halves, or nothing at all.** A type without a moment would be a
 * release whose age nobody knows; a moment without a type one that nobody can
 * tell came from an app or from an SMS. The two fields are only set together in
 * {@link deriveSecondFactor}, and whoever puts them back together should not do
 * that themselves in three places.
 */
export function secondFactorEvidenceOf(source) {
    return source.secondFactorAt !== null && source.secondFactorType !== null
        ? { at: source.secondFactorAt, type: source.secondFactorType }
        : null;
}
//# sourceMappingURL=second-factor.js.map